import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { BASE_API_URL } from "store/Me";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { statusBadgeOutlineClass, type StatusColor } from "shared/utils/statusChipColor";

export default function LockDatabaseHistory({ lockId }: { lockId: string }) {
  const { data, isError } = useGetLockDatabaseHistory(lockId);
  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>(
    {},
  );
  const [selectedRecord, setSelectedRecord] = React.useState<string | null>(null);
  const [showingAccessModal, setShowingAccessModal] = React.useState(false);
  const [expandedGuests, setExpandedGuests] = React.useState<Record<string, boolean>>({});
  const [downloadingRecordId, setDownloadingRecordId] = React.useState<
    string | null
  >(null);

  const {
    data: selectedRecordData,
    isLoading: isLoadingSelectedRecord,
    isError: isSelectedRecordError,
  } = useGetLockDatabaseRecord(lockId, selectedRecord);

  const {
    data: latestAccessSummary,
    isLoading: isLoadingLatestAccessSummary,
    isError: isLatestAccessSummaryError,
  } = useGetLatestLockAccessSummary(lockId, showingAccessModal);

  React.useEffect(() => {
    if (!showingAccessModal) {
      setExpandedGuests({});
    }
  }, [showingAccessModal]);

  const onDownloadRaw = async (databaseSyncRecordId: string) => {
    try {
      setDownloadingRecordId(databaseSyncRecordId);
      await downloadLockDatabaseRaw(lockId, databaseSyncRecordId);
    } catch (error) {
      console.error(error);
      window.alert("Unable to download database record.");
    } finally {
      setDownloadingRecordId(null);
    }
  };

  if (isError) {
    return <div>Error loading database history</div>;
  }

  if (data) {
    if (data.length === 0) {
      return <div>No records found</div>;
    }

    const mostRecentRecord = data[0];

    return (
      <div className="pb-5">
        <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col"></th>
                  <th scope="col">Timestamp</th>
                  <th scope="col">Completed</th>
                  <th scope="col">Status</th>
                  <th scope="col">Successful</th>
                  <th scope="col">Records</th>
                  <th scope="col">NxtDbVerTs</th>
                  <th scope="col"></th>
                  <th scope="col"></th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
            {data.map((history) => {
              const partials = history.partials ?? [];
              const isExpanded = expandedRows[history.id] ?? false;
              const isNewest = history.id === mostRecentRecord.id;

              return (
                <React.Fragment key={history.id}>
                  <tr style={isNewest ? { backgroundColor: "#e8f5e9" } : {}}>
                    <td>
                      {partials.length > 0 ? (
                        <div>
                          <Button
                            onClick={() =>
                              setExpandedRows((current) => ({
                                ...current,
                                [history.id]: !isExpanded,
                              }))
                            }
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            {isExpanded ? "Hide" : "Show"}
                          </Button>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                            {partials.length} partial{partials.length === 1 ? "" : "s"}
                          </div>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {formatDateTime(history.timestamp)}
                      {isNewest && (
                        <div style={{ fontSize: 11, color: "#2e7d32", marginTop: 4 }}>
                          ← Most Recent
                        </div>
                      )}
                    </td>
                    <td>{formatDateTime(history.completedTimestamp)}</td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <StatusBadge status={history.status} />
                        {history.isManual ? (
                          <Badge
                            className={statusBadgeOutlineClass("primary")}
                            variant="outline"
                          >
                            Manual
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td>{renderBoolean(history.successful)}</td>
                    <td>{history.records}</td>
                    <td>{formatDatabaseVersionTimestamp(history.nxtDbVerTs)}</td>
                    <td>
                      <Button
                        onClick={() => setSelectedRecord(history.id)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        View
                      </Button>
                    </td>
                    <td>
                      <Button
                        onClick={() => onDownloadRaw(history.id)}
                        disabled={downloadingRecordId === history.id}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        {downloadingRecordId === history.id ? "Downloading..." : "Download"}
                      </Button>
                    </td>
                    {isNewest ? (
                      <td>
                        <Button
                          onClick={() => setShowingAccessModal(true)}
                          size="sm"
                          type="button"
                        >
                          Estimate Access
                        </Button>
                      </td>
                    ) : (
                      <td></td>
                    )}
                  </tr>
                  {isExpanded
                    ? partials.map((partial) => (
                        <tr
                          key={partial.id}
                          style={{
                            backgroundColor: "#f3f4f6",
                            borderLeft: "4px solid #4b5563",
                            borderRight: "4px solid #4b5563",
                          }}
                        >
                          <td style={{ borderLeft: "4px solid #4b5563" }}>
                            {EMPTY_PARTIAL_CELL}
                          </td>
                          <td>{formatDateTime(partial.timestamp, EMPTY_PARTIAL_CELL)}</td>
                          <td>
                            {formatDateTime(
                              partial.completedTimestamp,
                              EMPTY_PARTIAL_CELL,
                            )}
                          </td>
                          <td>
                            <StatusBadge status={partial.status} />
                          </td>
                          <td>{renderBoolean(partial.successful, EMPTY_PARTIAL_CELL)}</td>
                          <td>
                            +{partial.addedRecords ?? 0} / ~
                            {partial.updatedRecords ?? 0} / -
                            {partial.deletedRecords ?? 0}
                          </td>
                          <td>
                            {formatDatabaseVersionTimestamp(
                              partial.nxtDbVerTs,
                              EMPTY_PARTIAL_CELL,
                            )}
                          </td>
                          <td>
                            <Button
                              onClick={() => setSelectedRecord(partial.id)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              View
                            </Button>
                          </td>
                          <td style={{ borderRight: "4px solid #4b5563" }}>
                            <Button
                              onClick={() => onDownloadRaw(partial.id)}
                              disabled={downloadingRecordId === partial.id}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              {downloadingRecordId === partial.id
                                ? "Downloading..."
                                : "Download"}
                            </Button>
                          </td>
                          <td style={{ borderRight: "4px solid #4b5563" }}>
                            {EMPTY_PARTIAL_CELL}
                          </td>
                        </tr>
                      ))
                    : null}
                </React.Fragment>
              );
            })}
            </tbody>
          </table>
          </div>
        </div>

        <Dialog
          open={Boolean(selectedRecord)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setSelectedRecord(null);
            }
          }}
        >
          <DialogContent
            aria-describedby={undefined}
            showCloseButton={false}
            className="sm:max-w-4xl"
          >
            <DialogHeader>
              <DialogTitle>Database Record</DialogTitle>
            </DialogHeader>
            {isLoadingSelectedRecord ? <div>Loading database record...</div> : null}
            {isSelectedRecordError ? <div>Error loading database record</div> : null}
            {selectedRecordData ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <strong>Lock:</strong> {selectedRecordData.lockName} <br />
                  <strong>Record Timestamp:</strong>{" "}
                  {formatDateTime(selectedRecordData.timestamp)}
                </div>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: "60vh",
                    overflow: "auto",
                    margin: 0,
                  }}
                >
                  {formatDatabasePayload(selectedRecordData.rawDatabase)}
                </pre>
              </>
            ) : null}
            <DialogFooter>
            {selectedRecord ? (
              <Button
                onClick={() => onDownloadRaw(selectedRecord)}
                disabled={downloadingRecordId === selectedRecord}
                type="button"
                variant="outline"
              >
                {downloadingRecordId === selectedRecord
                  ? "Downloading..."
                  : "Download"}
              </Button>
            ) : null}
              <Button onClick={() => setSelectedRecord(null)} type="button" variant="outline">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showingAccessModal}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setShowingAccessModal(false);
            }
          }}
        >
          <DialogContent
            aria-describedby={undefined}
            showCloseButton={false}
            className="sm:max-w-4xl"
          >
            <DialogHeader>
              <DialogTitle>Current Door Access (Latest Snapshot)</DialogTitle>
            </DialogHeader>
            {isLoadingLatestAccessSummary ? (
              <div>Loading current access summary...</div>
            ) : null}
            {isLatestAccessSummaryError ? (
              <div>Error loading current access summary</div>
            ) : null}
            {latestAccessSummary ? (
              <>
                <div
                  style={{
                    backgroundColor: "#fff3cd",
                    padding: 12,
                    borderRadius: 4,
                    marginBottom: 16,
                    fontSize: 13,
                  }}
                >
                  <strong>📋 About This Data</strong>
                  <p style={{ marginBottom: 8 }}>
                    This is a <strong>best-guess snapshot</strong> based on currently assigned
                    credentials. It combines the latest successful full database update with{" "}
                    <strong>{latestAccessSummary.partialsIncluded}</strong> partial update
                    {latestAccessSummary.partialsIncluded === 1 ? "" : "s"}. The estimate is as
                    recent as <strong>{formatDateTime(latestAccessSummary.latestPartialTimestamp || latestAccessSummary.fullDatabaseTimestamp)}</strong>.
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    Blocked credentials (shown below) are revocation entries represented as
                    physical keys and may include the last known assignee.
                  </p>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <strong>Lock:</strong> {latestAccessSummary.lockName} <br />
                  <strong>Latest Full Database:</strong>{" "}
                  {formatDateTime(latestAccessSummary.fullDatabaseTimestamp)} <br />
                  {latestAccessSummary.latestPartialTimestamp && (
                    <>
                      <strong>Latest Partial Update:</strong>{" "}
                      {formatDateTime(latestAccessSummary.latestPartialTimestamp)} <br />
                    </>
                  )}
                  <strong>Total credentials:</strong> {latestAccessSummary.totalCredentials} |{" "}
                  <strong>Resolved:</strong> {latestAccessSummary.resolvedCredentials} |{" "}
                  <strong>Unique guests:</strong> {latestAccessSummary.uniqueGuests}
                </div>

                <div style={{ marginBottom: 12 }}>
                  {(() => {
                    const allGuestIds = latestAccessSummary.guests.map((g) => g.guestId);
                    const hasGuests = allGuestIds.length > 0;
                    const allExpanded = hasGuests && allGuestIds.every((id) => expandedGuests[id]);
                    return (
                      <Button
                        disabled={!hasGuests}
                        onClick={() => {
                          if (allExpanded) {
                            setExpandedGuests({});
                          } else {
                            const expandedMap = Object.fromEntries(
                              allGuestIds.map((id) => [id, true])
                            );
                            setExpandedGuests(expandedMap);
                          }
                        }}
                          size="sm"
                          type="button"
                          variant="outline"
                      >
                        {allExpanded ? "Collapse All" : "Expand All"}
                      </Button>
                    );
                  })()}
                </div>

                <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3" style={{ marginBottom: 16 }}>
                  <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                    <tr>
                      <th scope="col">Guest</th>
                      <th scope="col">Credential Type</th>
                      <th scope="col">User ID</th>
                      <th scope="col">Prime Credential</th>
                      <th scope="col">Valid To</th>
                      <th scope="col">More</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                    {latestAccessSummary.guests.flatMap((guest) => {
                      const isExpanded = expandedGuests[guest.guestId] ?? false;
                      const firstCred = guest.credentials[0];
                      const remainingCreds = guest.credentials.slice(1);
                      const rows = [];

                      // Main row with first credential
                      rows.push(
                        <tr key={guest.guestId}>
                          <td>{guest.guestName}</td>
                          <td>{firstCred?.credentialOwnerType ?? "Unknown"}</td>
                          <td>{firstCred?.userId ?? "N/A"}</td>
                          <td>{firstCred?.primeCr ?? "N/A"}</td>
                          <td>
                            {firstCred?.validTo
                              ? formatDateTime(firstCred.validTo)
                              : "N/A"}
                          </td>
                          <td>
                            {remainingCreds.length > 0 ? (
                              <Button
                                onClick={() =>
                                  setExpandedGuests((prev) => ({
                                    ...prev,
                                    [guest.guestId]: !isExpanded,
                                  }))
                                }
                                size="sm"
                                type="button"
                                variant="outline"
                              >
                                {isExpanded
                                  ? `Hide ${remainingCreds.length}`
                                  : `${remainingCreds.length} more`}
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );

                      // Additional rows when expanded
                      if (isExpanded) {
                        remainingCreds.forEach((cred, idx) => {
                          rows.push(
                            <tr
                              key={`${guest.guestId}-expanded-${idx}`}
                              style={{ backgroundColor: "#f9f9f9" }}
                            >
                              <td colSpan={1}></td>
                              <td>{cred.credentialOwnerType ?? "Unknown"}</td>
                              <td>{cred.userId}</td>
                              <td>{cred.primeCr}</td>
                              <td>
                                {cred.validTo ? formatDateTime(cred.validTo) : "N/A"}
                              </td>
                              <td></td>
                            </tr>
                          );
                        });
                      }

                      return rows;
                    })}
                  </tbody>
                </table>

                {(() => {
                  const blockedCredentials = latestAccessSummary.unresolvedCredentials.filter(
                    (credential) => {
                      const normalizedFunction = (credential.fnctn ?? "").toLowerCase();
                      return (
                        normalizedFunction === "blocked" ||
                        !!credential.physicalKeyId ||
                        !!credential.physicalKeyCredential
                      );
                    }
                  );

                  const trulyUnresolvedCredentials =
                    latestAccessSummary.unresolvedCredentials.filter(
                      (credential) => !blockedCredentials.includes(credential)
                    );

                  return (
                    <>
                      {blockedCredentials.length > 0 ? (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <strong>
                              ⚠️ Blocked Credentials ({blockedCredentials.length})
                            </strong>
                          </div>
                          <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3" style={{ marginBottom: 16 }}>
                            <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                              <tr>
                                <th scope="col">Credential Type</th>
                                <th scope="col">PrimeCr</th>
                                <th scope="col">Physical Key</th>
                                <th scope="col">Last Assigned To</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                              {blockedCredentials.map((credential) => (
                                <tr key={`${credential.primeCr}-${credential.userId}`}>
                                  <td>{credential.credentialOwnerType ?? "Blocked"}</td>
                                  <td>{credential.primeCr}</td>
                                  <td>
                                    {credential.physicalKeyCredential ??
                                      credential.physicalKeyId ??
                                      "N/A"}
                                  </td>
                                  <td>
                                    {credential.lastAssignedGuestName ??
                                      credential.lastAssignedGuestId ??
                                      "N/A"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : null}

                      {trulyUnresolvedCredentials.length > 0 ? (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <strong>
                              ⚠️ Other Unresolved Credentials ({trulyUnresolvedCredentials.length})
                            </strong>
                          </div>
                          <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
                            <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                              <tr>
                                <th scope="col">Credential Type</th>
                                <th scope="col">UserId</th>
                                <th scope="col">PrimeCr</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                              {trulyUnresolvedCredentials.map((credential) => (
                                <tr key={`${credential.primeCr}-${credential.userId}`}>
                                  <td>{credential.credentialOwnerType ?? "Unknown"}</td>
                                  <td>{credential.userId ?? "N/A"}</td>
                                  <td>{credential.primeCr}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : null}
                    </>
                  );
                })()}
              </>
            ) : null}
            <DialogFooter>
              <Button onClick={() => setShowingAccessModal(false)} type="button" variant="outline">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return <div>loading...</div>;
}

interface LockDatabaseHistoryResponse {
  id: string;
  timestamp: string;
  completedTimestamp: string;
  status: string;
  successful: boolean;
  fullUpdate: boolean;
  records: number;
  isManual: boolean;
  nxtDbVerTs: number;
  partials: {
    id: string;
    timestamp: string;
    completedTimestamp: string;
    status: string;
    successful: boolean;
    addedRecords: number;
    updatedRecords: number;
    deletedRecords: number;
    nxtDbVerTs: number;
  }[];
}

interface LockDatabaseRecordResponse {
  id: string;
  lockId: string;
  lockName: string;
  timestamp: string;
  rawDatabase: string;
}

interface LockDatabaseAccessCredentialResponse {
  userId: string;
  primeCr: string;
  fnctn: string | null;
  credentialOwnerType: string | null;
  guestId: string | null;
  guestName: string | null;
  physicalKeyId: string | null;
  physicalKeyCredential: string | null;
  lastAssignedGuestId: string | null;
  lastAssignedGuestName: string | null;
  validFrom: string | null;
  validTo: string | null;
}

interface LockDatabaseAccessGuestResponse {
  guestId: string;
  guestName: string;
  credentials: LockDatabaseAccessCredentialResponse[];
}

interface LockDatabaseAccessSummaryResponse {
  lockId: string;
  lockName: string;
  fullDatabaseSyncRecordId: string;
  fullDatabaseTimestamp: string;
  partialsIncluded: number;
  latestPartialTimestamp: string | null;
  totalCredentials: number;
  resolvedCredentials: number;
  uniqueGuests: number;
  guests: LockDatabaseAccessGuestResponse[];
  unresolvedCredentials: LockDatabaseAccessCredentialResponse[];
}

async function downloadLockDatabaseRaw(
  lockId: string,
  databaseSyncRecordId: string,
) {
  const response = await axios.get<Blob>(
    `${BASE_API_URL}/api/Locks/${lockId}/database-history/${databaseSyncRecordId}/database-raw`,
    { responseType: "blob" },
  );

  const filename =
    extractFileNameFromContentDisposition(response.headers["content-disposition"]) ||
    `database_${databaseSyncRecordId}.json`;

  triggerBrowserDownload(response.data, filename);
}

async function getLockDatabaseHistory(lockId: string) {
  const response = await axios.get<LockDatabaseHistoryResponse[]>(
    `${BASE_API_URL}/api/Locks/${lockId}/database-history`
  );
  return response.data;
}

function useGetLockDatabaseHistory(lockId: string) {
  return useQuery({
    queryKey: ["locks", "details", "database", lockId],
    queryFn: () => getLockDatabaseHistory(lockId),
  });
}

async function getLockDatabaseRecord(lockId: string, databaseSyncRecordId: string) {
  const response = await axios.get<LockDatabaseRecordResponse>(
    `${BASE_API_URL}/api/Locks/${lockId}/database-history/${databaseSyncRecordId}/database`,
  );
  return response.data;
}

function useGetLockDatabaseRecord(lockId: string, databaseSyncRecordId: string | null) {
  return useQuery({
    queryKey: ["locks", "details", "database", "record", lockId, databaseSyncRecordId],
    queryFn: () => getLockDatabaseRecord(lockId, databaseSyncRecordId!),
    enabled: Boolean(databaseSyncRecordId),
  });
}

async function getLatestLockAccessSummary(lockId: string) {
  const response = await axios.get<LockDatabaseAccessSummaryResponse>(
    `${BASE_API_URL}/api/Locks/${lockId}/database-history/latest/access-summary`,
  );
  return response.data;
}

function useGetLatestLockAccessSummary(lockId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["locks", "details", "database", "latest-access", lockId],
    queryFn: () => getLatestLockAccessSummary(lockId),
    enabled,
  });
}

function formatDateTime(
  value: string | null | undefined,
  emptyValue: string = EMPTY_CELL,
) {
  if (!value) {
    return emptyValue;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatDatabaseVersionTimestamp(
  value: number | null | undefined,
  emptyValue: string = EMPTY_CELL,
) {
  if (value === null || value === undefined) {
    return emptyValue;
  }

  return `${value}`;
}

function renderBoolean(
  value: boolean | null | undefined,
  emptyValue: string = EMPTY_CELL,
) {
  if (value === null || value === undefined) {
    return emptyValue;
  }

  return value ? "Yes" : "No";
}

const EMPTY_CELL = "N/A";
const EMPTY_PARTIAL_CELL = "";

function getStatusBadgeColor(status: string): StatusColor {
  const normalizedStatus = status.toLowerCase();

  if (
    normalizedStatus.includes("success") ||
    normalizedStatus.includes("complete") ||
    normalizedStatus.includes("completed")
  ) {
    return "success";
  }

  if (
    normalizedStatus.includes("fail") ||
    normalizedStatus.includes("error") ||
    normalizedStatus.includes("failed")
  ) {
    return "error";
  }

  if (
    normalizedStatus.includes("pending") ||
    normalizedStatus.includes("running") ||
    normalizedStatus.includes("progress")
  ) {
    return "primary";
  }

  return "default";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      className={statusBadgeOutlineClass(getStatusBadgeColor(status))}
      variant="outline"
    >
      {status}
    </Badge>
  );
}

function formatDatabasePayload(rawDatabase: string) {
  try {
    const parsed = JSON.parse(rawDatabase);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return rawDatabase;
  }
}

function extractFileNameFromContentDisposition(contentDisposition?: string) {
  if (!contentDisposition) {
    return null;
  }

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FileNameMatch?.[1]) {
    return decodeURIComponent(utf8FileNameMatch[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1];
  }

  return null;
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
